// Renders an episode's soundtrack offline, through the same voice chains as playback, and encodes a WAV.
import type { Timeline } from "../../engine/timeline";
import { segClips, type AudioClip, type UrlOf, type VoiceOf } from "../playback";
import { VOICE_LATENCY, buildVoiceChain, prepareVoice } from "./voice";

export const RATE = 48000;

// Lines start exactly where the timeline puts them.
export const renderSoundtrack = (tl: Timeline, duration: number, urlOf: UrlOf, voiceOf: VoiceOf) => renderClips(segClips(tl, urlOf), duration, voiceOf);

// The same for any list of clips (timeline episodes): each starts at its `start`, clips may overlap.
export async function renderClips(lines: AudioClip[], duration: number, voiceOf: VoiceOf): Promise<AudioBuffer> {
  // Render VOICE_LATENCY longer and drop that much from the front, which cancels the chains' delay.
  const pre = Math.round(VOICE_LATENCY * RATE);
  const ctx = new OfflineAudioContext(1, Math.ceil(duration * RATE) + pre, RATE);
  await prepareVoice(ctx);
  const buffers = await Promise.all(lines.map(s => fetch(s.url).then(r => {
    if (!r.ok) throw new Error(`Couldn't load a line's audio (${r.status})`);
    return r.arrayBuffer();
  }).then(b => ctx.decodeAudioData(b))));
  const buses = new Map<string, GainNode>();
  for (const [i, s] of lines.entries()) {
    const speaker = s.speaker;
    let bus = buses.get(speaker);
    if (!bus) {
      bus = ctx.createGain();
      buildVoiceChain(ctx, voiceOf(speaker), bus, ctx.destination);
      buses.set(speaker, bus);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffers[i]!;
    src.connect(bus);
    src.start(s.start);
  }
  const full = await ctx.startRendering();
  const out = new AudioBuffer({ length: full.length - pre, numberOfChannels: 1, sampleRate: RATE });
  out.copyToChannel(full.getChannelData(0).subarray(pre), 0);
  return out;
}

// 16-bit PCM WAV, first channel only.
export function encodeWav(buf: AudioBuffer): Blob {
  const data = buf.getChannelData(0), n = data.length, rate = buf.sampleRate;
  const view = new DataView(new ArrayBuffer(44 + n * 2));
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  str(0, "RIFF"); view.setUint32(4, 36 + n * 2, true); str(8, "WAVE");
  str(12, "fmt "); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  str(36, "data"); view.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, data[i]!));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([view.buffer], { type: "audio/wav" });
}
